import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';

import AnnotationTooltip from './AnnotationTooltip';
import {
  MarkerContainer,
  FlagSvg,
  AnnotationLine,
} from './AnnotationMarker.styled';

// A point-in-time annotation, drawn as a small pennant flag coloured by type
// (the dashed drop-line through the plot is rendered separately by the overlay).
function AnnotationMarker(props) {
  const {
    annotation,
    position,
    color,
    showControls = true,
    showLine = false,
    lineHeight = 100,
    onClick
  } = props;

  const [showTooltip, setShowTooltip] = useState(false);
  const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });

  const handleMouseEnter = useCallback((event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const viewportTopThreshold = 140;
    const placement = rect.top < viewportTopThreshold ? 'bottom' : 'top';
    const y = placement === 'top' ? rect.top : rect.bottom;

    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const margin = 12;
    const tooltipMaxWidth = 360;
    const centerX = rect.left + rect.width / 2;

    let align = 'center';
    let x = centerX;
    if (centerX - tooltipMaxWidth / 2 < margin) {
      align = 'left';
      x = Math.max(margin, rect.left);
    } else if (centerX + tooltipMaxWidth / 2 > viewportWidth - margin) {
      align = 'right';
      x = Math.min(viewportWidth - margin, rect.right);
    }

    setShowTooltip(true);
    setTooltipPosition({ x, y, placement, align });
  }, []);

  const handleMouseLeave = useCallback(() => {
    setShowTooltip(false);
  }, []);

  const handleTooltipMouseEnter = useCallback(() => {
    setShowTooltip(prev => (prev ? true : prev));
  }, []);

  const handleTooltipMouseLeave = useCallback(() => {
    setShowTooltip(false);
  }, []);

  const handleClick = useCallback((event) => {
    if (onClick) onClick(event);
  }, [onClick]);

  return (
    <>
      <MarkerContainer
        style={{
          left: position.x,
          top: position.y
        }}
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        onClick={handleClick}
      >
        {/* Pennant flag on a short pole, tinted by the annotation colour. */}
        <FlagSvg width="14" height="18" viewBox="0 0 14 18" aria-hidden>
          <line x1="1.5" y1="0.5" x2="1.5" y2="17.5" stroke={color} strokeWidth="2" strokeLinecap="round" />
          <path d="M2.5,1 L13,1 L9.8,4.75 L13,8.5 L2.5,8.5 Z" fill={color} />
        </FlagSvg>

        <AnnotationLine
          color={color}
          height={lineHeight}
          show={showLine}
        />
      </MarkerContainer>

      {showTooltip && (
        <AnnotationTooltip
          annotation={annotation}
          position={tooltipPosition}
          showControls={showControls}
          onClose={handleMouseLeave}
          onMouseEnter={handleTooltipMouseEnter}
          onMouseLeave={handleTooltipMouseLeave}
        />
      )}
    </>
  );
}

AnnotationMarker.propTypes = {
  annotation: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    type: PropTypes.string.isRequired,
    start_time: PropTypes.string.isRequired,
    end_time: PropTypes.string,
    description: PropTypes.string,
    severity: PropTypes.oneOf(['low', 'medium', 'high', 'critical']),
    created_by: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    created_at: PropTypes.string,
    base_metadata: PropTypes.object,
    user_metadata: PropTypes.object
  }).isRequired,

  position: PropTypes.shape({
    x: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    y: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired
  }).isRequired,

  color: PropTypes.string.isRequired,

  showControls: PropTypes.bool,
  showLine: PropTypes.bool,
  lineHeight: PropTypes.number,

  onClick: PropTypes.func
};

AnnotationMarker.defaultProps = {
  showControls: true,
  showLine: false,
  lineHeight: 100,
  onClick: null
};

export default AnnotationMarker;
